/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import {
	BackgroundContainer,
	FAQ,
	LinkList,
	ResponsiveContainer,
	SectionContainer,
	SectionHeading,
} from "@components/elements";
import { useDebounce } from "@components/hooks";
import { cleanFAQTab, FAQButtonInput, FAQItem, hasFAQHash, mapFAQButtons } from "@components/utils/faq.utils";
import { buildFaqPageJsonLd } from "@components/utils/faq-structured-data.utils";
import { scrollIntoViewEffect } from "@components/utils/scroll.utils";
import { extractBackgroundContainerProps, extractSectionContainerProps } from "@components/utils/section.utils";
import { FaqSearchSectionFragment, FaqTabElementFragment } from "@cosmos/hygraph-schema";
import { SearchInput } from "@eds/react";
import { useLocation } from "@epic-mw/app-context";
import { useMessages } from "@epic-mw/i18n";
import { css } from "@panda/css";
import { Box, Flex, VStack } from "@panda/jsx";
import { MouseEvent, useCallback, useEffect, useMemo, useState } from "react";

const styles = css({
	marginInline: "auto",
	width: "100%",
	flexDirection: "row",
	columnGap: "eds.dimension.32",
	rowGap: "eds.dimension.48",
	padding: "eds.dimension.48",
	border: "token(spacing.eds.dimension.1) solid token(colors.eds.palette.transparent.light.005)",
	backgroundColor: "eds.palette.transparent.light.005",
	backdropFilter: "var(--effect-material-blur, blur(50px))",
	borderRadius: "eds.borderRadius.24",
	mdDown: {
		flexDirection: "column",
	},
	smDown: {
		columnGap: "eds.dimension.24",
		rowGap: "eds.dimension.24",
		paddingInline: "eds.dimension.24",
		paddingBlock: "eds.dimension.40",
	},
});

const faqStyles = css({
	marginInline: "auto",
	maxWidth: "52rem",
	width: "100%",
});

const currentTabStyles = css({ textDecoration: "underline" });
const disableTabStyles = css({
	"&&": { color: "eds.text.disabled.default", pointerEvents: "none", cursor: "default" },
});

export const FAQSearchSection = ({
	config,
	faqTabs,
	title,
	desc,
	modButtons,
	faqButtonsPosition,
}: FaqSearchSectionFragment): React.JSX.Element | null => {
	const messages = useMessages();
	const [query, setQuery] = useState("");
	const debouncedQuery = useDebounce(query);
	const location = useLocation();
	const [currentIndex, setCurrentIndex] = useState(0);

	const faqItems = useMemo(() => faqTabs?.map(cleanFAQTab) || [], [faqTabs]);

	// FAQPage schema.org JSON-LD covering every tab's Q&A (all of it is on the page,
	// just behind tabs/search), built independently of the search/tab UI state so the
	// structured data stays stable and matches the visible content (Google requirement).
	const faqJsonLd = useMemo(() => buildFaqPageJsonLd(faqItems.flatMap((tab) => tab.items)), [faqItems]);

	const [locationHash, setLocationHash] = useState<string>(location?.hash || "");
	useEffect(() => {
		if (!locationHash?.startsWith("#") || locationHash.length < 2) return;

		const hash = locationHash.slice(1);
		const index = faqItems.findIndex((el) => hasFAQHash(el, hash) || el?.items?.some((item) => hasFAQHash(item, hash)));

		if (index < 0) return;
		setCurrentIndex(index);

		const newEl = faqItems[index];
		if (!hasFAQHash(newEl, hash)) return;

		return scrollIntoViewEffect(newEl?.anchorLinkId || newEl?.fallbackLinkId);
	}, [locationHash, faqItems]);

	useEffect(() => {
		if (locationHash) return; // run only on first hash
		if (!location?.hash) return;
		setLocationHash(location?.hash);
	}, [location, locationHash]);

	const handleLinkClick = useCallback(
		(event: MouseEvent<HTMLAnchorElement>, index: number) => {
			if (window.self !== window.top) event?.preventDefault();
			setCurrentIndex(index);
		},
		[setCurrentIndex],
	);

	const currentFaq = useMemo(() => ({ ...faqItems[currentIndex] }), [currentIndex, faqItems]);
	const compareQuery = useMemo(() => debouncedQuery?.toLowerCase() || "", [debouncedQuery]);

	const filterItem = useCallback(
		({ answer, question }: FAQItem | FaqTabElementFragment["items"][number]): boolean => {
			return (
				!compareQuery?.length ||
				answer?.html?.toLowerCase().includes(compareQuery) ||
				question?.html?.toLowerCase().includes(compareQuery) ||
				false
			);
		},
		[compareQuery],
	);

	const currentItems = useMemo(() => {
		return currentFaq?.items?.filter(filterItem);
	}, [currentFaq, filterItem]);

	const processedLinks = useMemo(
		() =>
			faqItems.map(({ title, id, items, fallbackLinkId, anchorLinkId }, index) => {
				const disabled = !items.some(filterItem);
				const classNameStyles = index === currentIndex ? currentTabStyles : undefined;
				return {
					id,
					disabled,
					href: disabled ? undefined : `#${anchorLinkId || fallbackLinkId}`,
					label: title,
					searchLabel: title?.toLowerCase(),
					className: disabled ? disableTabStyles : classNameStyles,
				};
			}),
		[faqItems, filterItem, currentIndex],
	);

	useEffect(() => {
		const currentIndexFilteredOut = currentIndex < 0 || processedLinks[currentIndex]?.disabled;
		if (currentIndexFilteredOut) {
			if (compareQuery?.length) {
				const foundLabelIndex = processedLinks.findIndex(
					(el) => el.searchLabel?.includes(compareQuery) && !el.className,
				);
				if (foundLabelIndex > -1) {
					setCurrentIndex(foundLabelIndex);
					return;
				}
			}
			const firstAvailable = processedLinks.findIndex((el) => !el.className);
			if (firstAvailable > -1) {
				setCurrentIndex(firstAvailable);
			}
		}
	}, [processedLinks, currentIndex, compareQuery, setCurrentIndex]);

	const buttonsSource: FAQButtonInput[] = useMemo(() => (modButtons?.length ? modButtons : []), [modButtons]);
	const cosmosButtons = useMemo(() => mapFAQButtons(buttonsSource, "FAQ-Search"), [buttonsSource]);

	const validHeading = Boolean(config?.heading?.title || config?.heading?.desc);
	const validContent = Boolean(faqTabs?.length);
	if (!validHeading && !validContent) return null;

	return (
		<>
			{faqJsonLd ? <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqJsonLd }} /> : null}
			<BackgroundContainer {...extractBackgroundContainerProps(config)}>
				<ResponsiveContainer>
					<SectionContainer {...extractSectionContainerProps(config)}>
						<VStack alignItems="stretch" gap="eds.dimension.48" className="faq-search-section">
							{validHeading ? <SectionHeading {...config?.heading} /> : null}
							{validContent ? (
								<Flex className={styles} id={currentFaq?.anchorLinkId || currentFaq?.fallbackLinkId}>
									<VStack alignItems="stretch" gap="eds.dimension.24" flexBasis="45%">
										<SectionHeading
											desc={desc}
											// h2, not h3: when the section has no top-level config.heading, this panel
											// title is the first heading under the page hero h1 (h3 would skip h2).
											title={{ ...title, as: "h2", variant: "headingLG" }}
											compressedTitle={true}
											titleDescStack={{ gap: "eds.dimension.16" }}
										/>
										<SearchInput
											label={messages["epic.cosmos.search.placeholder"] || "Search..."}
											defaultValue={query || ""}
											onChange={setQuery}
											clearLabel={messages["epic.cosmos.search.clear.button"] || "Clear search"}
										/>
										{processedLinks?.length > 1 ? (
											<Box>
												<LinkList links={processedLinks} onClick={handleLinkClick} linkVariant="paragraphSM" />
											</Box>
										) : null}
									</VStack>
									<div className={faqStyles}>
										<FAQ
											items={currentItems}
											title={currentFaq?.title || ""}
											id={currentFaq?.id || ""}
											titleVariant="headingLG"
											buttons={cosmosButtons}
											buttonsPosition={faqButtonsPosition || undefined}
											firstItemDefaultOpen={currentFaq?.firstItemDefaultOpen}
										/>
									</div>
								</Flex>
							) : null}
						</VStack>
					</SectionContainer>
				</ResponsiveContainer>
			</BackgroundContainer>
		</>
	);
};
